import {
	DownloadOutlined,
	FireOutlined,
	RiseOutlined,
	TrophyOutlined,
	UserOutlined,
} from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Button, DatePicker } from "antd";
import { useMemo, useState } from "react";
import { formatMoney } from "@/shared/utils/money";
import { getManagersReport } from "../services/managers.api";

const { RangePicker } = DatePicker;

// ─── Types ────────────────────────────────────────────────────────────────────

export type { ManagerStat } from "../services/managers.api";

// ─── Mock data (not used anymore) ─────────────────────────────────────────────

// ─── Mock data (not used anymore) ─────────────────────────────────────────────

/*
const allManagers: ManagerStat[] = [
	{
		id: "m1",
		name: "Bobur Toshmatov",
		initials: "BT",
		colorVariant: "blue",
		building: "Sky Tower",
		totalSales: 34,
		totalAmount: 4820,
		collectedAmount: 3220,
		collectionRate: 66.8,
		avgDealSize: 141.8,
	},
	{
		id: "m2",
		name: "Kamola Yusupova",
		initials: "KY",
		colorVariant: "violet",
		building: "Green Park",
		totalSales: 29,
		totalAmount: 3960,
		collectedAmount: 2350,
		collectionRate: 59.3,
		avgDealSize: 136.6,
	},
	{
		id: "m3",
		name: "Jasur Mirzayev",
		initials: "JM",
		colorVariant: "green",
		building: "Sky Tower",
		totalSales: 26,
		totalAmount: 3290,
		collectedAmount: 1820,
		collectionRate: 55.3,
		avgDealSize: 126.5,
	},
	{
		id: "m4",
		name: "Nilufar Rashidova",
		initials: "NR",
		colorVariant: "sky",
		building: "Comfort Home",
		totalSales: 21,
		totalAmount: 2640,
		collectedAmount: 1420,
		collectionRate: 53.8,
		avgDealSize: 125.7,
	},
	{
		id: "m5",
		name: "Otabek Hamidov",
		initials: "OH",
		colorVariant: "amber",
		building: "Comfort Home",
		totalSales: 17,
		totalAmount: 1980,
		collectedAmount: 980,
		collectionRate: 49.5,
		avgDealSize: 116.5,
	},
	{
		id: "m6",
		name: "Sherzod Tursunov",
		initials: "ST",
		colorVariant: "blue",
		building: "Green Park",
		totalSales: 13,
		totalAmount: 1540,
		collectedAmount: 680,
		collectionRate: 44.2,
		avgDealSize: 118.5,
	},
];
*/

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Yig'ish % uchun rang */
function getRateColor(rate: number): { bg: string; color: string } {
	if (rate >= 60) {
		return { bg: "var(--crm-green-soft)", color: "#0b7a45" };
	}
	if (rate >= 50) {
		return { bg: "var(--crm-amber-soft)", color: "#9a6a00" };
	}
	return { bg: "var(--crm-red-soft)", color: "#b32626" };
}

/** Rank belgisi */
function RankBadge({ rank }: { rank: number }) {
	const medals: Record<number, { emoji: string; title: string }> = {
		1: { emoji: "🥇", title: "1-o'rin" },
		2: { emoji: "🥈", title: "2-o'rin" },
		3: { emoji: "🥉", title: "3-o'rin" },
	};
	const medal = medals[rank];
	if (medal) {
		return (
			<span title={medal.title} style={{ fontSize: 18, lineHeight: 1 }}>
				{medal.emoji}
			</span>
		);
	}
	return <span style={{ color: "var(--crm-ink-3)", fontSize: 13, fontWeight: 700 }}>{rank}</span>;
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function ManagersRatingPage() {
	const [dates, setDates] = useState<[string, string] | undefined>();

	const { data: allManagers = [], isLoading } = useQuery({
		queryKey: ["managers-report", dates],
		queryFn: () =>
			getManagersReport({
				dateFrom: dates?.[0],
				dateTo: dates?.[1],
			}),
	});

	const filtered = useMemo(() => {
		return [...allManagers].sort((a, b) => b.totalSales - a.totalSales);
	}, [allManagers]);

	const totalSales = filtered.reduce((s, m) => s + m.totalSales, 0);
	const totalAmount = filtered.reduce((s, m) => s + m.totalAmount, 0);
	const totalCollected = filtered.reduce((s, m) => s + m.collectedAmount, 0);
	const avgRate =
		filtered.length > 0
			? Math.round(filtered.reduce((s, m) => s + m.collectionRate, 0) / filtered.length)
			: 0;

	return (
		<div className="crm-managers-report">
			{/* ── Header ── */}
			<div className="crm-page-head">
				<div>
					<h1>Menejerlar reytingi</h1>
					<p>Sotuv natijalari va yig'ish ko'rsatkichlari · </p>
				</div>
				<div className="crm-report-actions">
					<RangePicker
						className="crm-report-range"
						onChange={(val) => {
							if (val?.[0] && val[1]) {
								setDates([val[0].format("YYYY-MM-DD"), val[1].format("YYYY-MM-DD")]);
							} else {
								setDates(undefined);
							}
						}}
					/>
					<Button icon={<DownloadOutlined />}>Eksport</Button>
				</div>
			</div>

			{/* ── KPI ── */}
			<section className="crm-report-kpis">
				<div className="crm-kpi crm-report-kpi">
					<div className="crm-kpi-title">
						<span className="crm-kpi-icon blue">
							<UserOutlined />
						</span>
						Menejerlar
					</div>
					<div className="crm-kpi-value">
						<strong>{filtered.length}</strong>
						<span>nafar</span>
					</div>
					<div className="crm-kpi-note">aktiv menejerlar soni</div>
				</div>

				<div className="crm-kpi crm-report-kpi">
					<div className="crm-kpi-title">
						<span className="crm-kpi-icon green">
							<RiseOutlined />
						</span>
						Jami sotuv
					</div>
					<div className="crm-kpi-value">
						<strong>{totalSales}</strong>
						<span>ta</span>
					</div>
					<div className="crm-kpi-note">barcha menejerlar bo'yicha</div>
				</div>

				<div className="crm-kpi crm-report-kpi">
					<div className="crm-kpi-title">
						<span className="crm-kpi-icon violet">
							<TrophyOutlined />
						</span>
						Jami summa
					</div>
					<div className="crm-kpi-value">
						<strong>{formatMoney(totalAmount)}</strong>
					</div>
					<div className="crm-kpi-note">yig'ilgan {formatMoney(totalCollected)}</div>
				</div>

				<div className="crm-kpi crm-report-kpi">
					<div className="crm-kpi-title">
						<span className="crm-kpi-icon amber">
							<FireOutlined />
						</span>
						O'rt. yig'ish
					</div>
					<div className="crm-kpi-value">
						<strong>{avgRate}</strong>
						<span>%</span>
					</div>
					<div className="crm-kpi-note">
						{filtered[0] ? `Lider: ${filtered[0].name.split(" ")[0]}` : "—"}
					</div>
				</div>
			</section>

			{/* ── Table ── */}
			<div className="crm-card crm-table-card crm-managers-table-card">
				<div className="crm-card-head">
					<h3>Barcha menejerlar</h3>
					<span className="crm-badge blue">{filtered.length} ta</span>
				</div>

				{isLoading ? (
					<div style={{ padding: 40, textAlign: "center", color: "var(--crm-ink-3)" }}>
						Yuklanmoqda...
					</div>
				) : (
					<>
						<div className="crm-table-wrap">
							<table className="crm-table crm-managers-table">
								<thead>
									<tr>
										<th>#</th>
										<th>Menejer</th>
										<th style={{ textAlign: "right" }}>Sotuv</th>
										<th style={{ textAlign: "right" }}>Summa</th>
										<th style={{ textAlign: "right" }}>Yig'ilgan</th>
										<th style={{ textAlign: "center" }}>Yig'ish %</th>
										<th style={{ textAlign: "right" }}>O'rtacha</th>
									</tr>
								</thead>
								<tbody>
									{filtered.map((manager, i) => {
										const rateStyle = getRateColor(manager.collectionRate);
										return (
											<tr key={manager.id}>
												{/* # */}
												<td style={{ textAlign: "center" }}>
													<RankBadge rank={i + 1} />
												</td>

												{/* Menejer */}
												<td>
													<div style={{ display: "flex", alignItems: "center", gap: 10 }}>
														<div className={`crm-mini-avatar ${manager.colorVariant}`}>
															{manager.initials}
														</div>
														<span style={{ fontWeight: 700, fontSize: 13.5 }}>{manager.name}</span>
													</div>
												</td>

												{/* Sotuv */}
												<td style={{ textAlign: "right" }}>
													<strong className="num" style={{ fontSize: 14 }}>
														{manager.totalSales}
													</strong>
												</td>

												{/* Summa */}
												<td style={{ textAlign: "right" }}>
													<strong className="num" style={{ fontSize: 14 }}>
														{formatMoney(manager.totalAmount)}
													</strong>
												</td>

												{/* Yig'ilgan */}
												<td style={{ textAlign: "right" }}>
													<span className="num" style={{ color: "var(--crm-ink-2)", fontSize: 13 }}>
														{formatMoney(manager.collectedAmount)}
													</span>
												</td>

												{/* Yig'ish % — rangli bejdj */}
												<td style={{ textAlign: "center" }}>
													<span
														style={{
															display: "inline-block",
															padding: "3px 10px",
															borderRadius: 8,
															background: rateStyle.bg,
															color: rateStyle.color,
															fontWeight: 700,
															fontSize: 12.5,
															whiteSpace: "nowrap",
														}}
													>
														{manager.collectionRate.toFixed(1)}%
													</span>
												</td>

												{/* O'rtacha */}
												<td style={{ textAlign: "right" }}>
													<span className="num" style={{ color: "var(--crm-ink-2)", fontSize: 13 }}>
														{formatMoney(manager.avgDealSize)}
													</span>
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>
						</div>

						<div className="crm-table-foot">
							<span>Jami {filtered.length} ta menejer</span>
							<span style={{ color: "var(--crm-ink-3)", fontSize: 12 }}>
								Hisobot davri va KPI ko'rsatkichlari shu yerda jamlanadi
							</span>
						</div>
					</>
				)}
			</div>
		</div>
	);
}

export default ManagersRatingPage;
